/**
 * DecisionRecord schema (gibbs-decision-record-export, 2026-05-15).
 *
 * Versioned JSON shape capturing a chemist's decision at a specific
 * moment — the rationale they saw, the ranked compounds they chose
 * between, the option they selected, and reviewer metadata. Designed
 * as the canonical Aim-1 compliance artifact: archivable, parseable
 * years later, programmatically queryable by reviewer / target /
 * date.
 *
 * **Schema versioning policy**: never delete a field once shipped.
 * Deprecate via documentation; readers fall back to defaults when
 * old records lack newer fields. Adding a new required field is a
 * MAJOR version bump.
 *
 * @module gibbs/decision-records/schema
 */

export const SCHEMA_VERSION = '1.0';

/**
 * @typedef {Object} CompoundSnapshot
 * @property {number} id - Anonymous compound id at decision time
 * @property {number} [primaryScore] - Promoted primary score
 * @property {Object} [properties] - MW, logP, etc.
 * @property {number} [interactionCount]
 * @property {string[]|null} [flags] - Toxicophore flag IDs; null = scanner unavailable
 */

/**
 * @typedef {Object} DecisionContext
 * @property {string} triggerSurface - e.g. 'compound-compare', 'results-pane'
 * @property {Object|null} rationaleSnapshot - DecisionRationale instance
 * @property {CompoundSnapshot[]} rankedCompounds
 * @property {number|'none-of-the-above'|'defer'} userPicked
 * @property {string} [frame] - Question-chooser frame id, if set
 */

/**
 * @typedef {Object} Reviewer
 * @property {string} id - User id or email
 * @property {string} [displayName]
 * @property {string} [role]
 */

/**
 * @typedef {Object} DecisionRecord
 * @property {string} schemaVersion
 * @property {string} id - UUID
 * @property {string} createdAt - ISO-8601 timestamp
 * @property {Reviewer} reviewer
 * @property {{ proteinPdbId?: string, projectName?: string }} workspace
 * @property {DecisionContext} decisionContext
 * @property {string} reviewerNote
 * @property {string|null} signature - SHA-256 hash of the canonical body
 */

const REQUIRED_TOP_FIELDS = [
    'schemaVersion', 'id', 'createdAt', 'reviewer', 'workspace',
    'decisionContext', 'reviewerNote',
];

const REQUIRED_CONTEXT_FIELDS = [
    'triggerSurface', 'rankedCompounds', 'userPicked',
];

/**
 * Validate a candidate decision record against the v1 schema.
 *
 * @param {*} record
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateDecisionRecord(record) {
    const errors = [];
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
        return { valid: false, errors: ['record is not an object'] };
    }

    for (const f of REQUIRED_TOP_FIELDS) {
        if (record[f] === undefined || record[f] === null) {
            errors.push(`missing required field: ${f}`);
        }
    }

    if (typeof record.schemaVersion === 'string'
        && record.schemaVersion !== SCHEMA_VERSION) {
        errors.push(
            `schemaVersion mismatch: got ${record.schemaVersion}, `
            + `expected ${SCHEMA_VERSION}`
        );
    }

    if (typeof record.id !== 'string' || record.id.length === 0) {
        errors.push('id must be a non-empty string');
    }

    if (typeof record.createdAt === 'string') {
        const t = Date.parse(record.createdAt);
        if (Number.isNaN(t)) {
            errors.push('createdAt must be a parseable ISO-8601 timestamp');
        }
    }

    if (record.reviewer && typeof record.reviewer === 'object') {
        if (typeof record.reviewer.id !== 'string'
            || record.reviewer.id.length === 0) {
            errors.push('reviewer.id must be a non-empty string');
        }
    }

    if (record.decisionContext && typeof record.decisionContext === 'object') {
        for (const f of REQUIRED_CONTEXT_FIELDS) {
            if (record.decisionContext[f] === undefined) {
                errors.push(`decisionContext.${f} is required`);
            }
        }
        if (!Array.isArray(record.decisionContext.rankedCompounds)) {
            errors.push('decisionContext.rankedCompounds must be an array');
        }
    }

    return { valid: errors.length === 0, errors };
}

/**
 * Generate a stable v4-like UUID. Uses crypto.randomUUID() when
 * available (modern browsers + Node 14+); falls back to a Math.random
 * shim for ancient environments.
 *
 * @returns {string}
 */
export function generateId() {
    if (typeof globalThis !== 'undefined'
        && globalThis.crypto
        && typeof globalThis.crypto.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }
    // Fallback — adequate for client-side decision records but
    // never cryptographic.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Build a DecisionRecord with safe defaults. Caller provides the
 * fields they have; the rest take defaults.
 *
 * @param {Partial<DecisionRecord>} input
 * @returns {DecisionRecord}
 */
export function createDecisionRecord(input = {}) {
    return {
        schemaVersion: SCHEMA_VERSION,
        id: input.id || generateId(),
        createdAt: input.createdAt || new Date().toISOString(),
        reviewer: input.reviewer || { id: 'unknown' },
        workspace: input.workspace || {},
        decisionContext: input.decisionContext || {
            triggerSurface: 'unknown',
            rationaleSnapshot: null,
            rankedCompounds: [],
            userPicked: 'defer',
        },
        reviewerNote: input.reviewerNote || '',
        signature: input.signature || null,
    };
}
